import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CardActions,
  Box,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import { updateEmployeePassword } from "../../utils/Api";
import CustomBreadcrumbs from "../CustomBreadcrumbs";
import { useDispatch } from "react-redux";
import { setToasterStatusMsg } from "../../Redux/reportSlice";

const getPasswordStrength = (password) => {
  const criteria = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };

  const passedChecks = Object.values(criteria).filter(Boolean).length;

  if (passedChecks === 5) return "Strong";
  if (passedChecks >= 3) return "Moderate";
  return "Weak";
};

const ChangePassword = () => {
  const { id: employeeId } = useParams();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  console.log(location.state);

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(getPasswordStrength(newPassword));
  };

  const handleUpdatePassword = async () => {
    setLoading(true);
    try {
      await updateEmployeePassword(employeeId, password);

      dispatch(
        setToasterStatusMsg({
          msg: "Password updated successfully",
          severity: "success",
        })
      );
      setPassword("");
      setConfirmPassword("");
    } catch (error) {
      dispatch(
        setToasterStatusMsg({
          msg: error?.message || "Failed to update password",
          severity: "success",
        })
      );
    } finally {
      setLoading(false);
    }
  };
  const breadcrumbItems = [
    { label: "Home", path: "/cms/dashboard" },
    { label: "Manage Users", path: "/cms/users-list" },
  ];

  const isMatch = confirmPassword ? password === confirmPassword : null;

  return (
    <>
      <CustomBreadcrumbs
        items={breadcrumbItems}
        currentLabel={["Reset Password", location.state]}
      />
      <Card
        sx={{
          width: 400,
          padding: 2,
          boxShadow: 3,
          borderRadius: 2,
          textAlign: "center",
          margin: "auto",
          //   mt: 8,
        }}
      >
        <CardContent sx={{ padding: "10px" }}>
          <Typography variant="h5" fontWeight="bold" mb={2}>
            Reset Password
          </Typography>
          <Typography variant="caption" color="text.secondary">
            New password will be sent to the user's registered email.
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Enter Password"
            variant="outlined"
            margin="normal"
            size="small"
            value={password}
            onChange={handlePasswordChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {password && (
            <Typography
              variant="body2"
              sx={{
                color:
                  passwordStrength === "Strong"
                    ? "green"
                    : passwordStrength === "Moderate"
                    ? "orange"
                    : "red",
              }}
            >
              Strength: {passwordStrength}
            </Typography>
          )}

          <Box sx={{ textAlign: "left", mt: 1 }}>
            {[
              { label: "8 characters", condition: password.length >= 8 },
              {
                label: "1 uppercase letter",
                condition: /[A-Z]/.test(password),
              },
              {
                label: "1 lowercase letter",
                condition: /[a-z]/.test(password),
              },
              {
                label: "2 numbers",
                condition: (password.match(/\d/g) || []).length >= 2,
              },
              {
                label: "1 special character",
                condition: /[^A-Za-z0-9]/.test(password),
              },
            ].map((item, index) => (
              <Typography
                key={index}
                variant="body2"
                color={item.condition ? "green" : "gray"}
                sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 1 }}
              >
                {item.condition ? (
                  <CheckCircle fontSize="small" />
                ) : (
                  <Cancel fontSize="small" />
                )}{" "}
                {item.label}
              </Typography>
            ))}
          </Box>

          <TextField
            fullWidth
            type={showConfirmPassword ? "text" : "password"}
            label="Confirm Password"
            variant="outlined"
            margin="normal"
            size="small"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {confirmPassword && (
            <Typography
              variant="body2"
              sx={{ color: isMatch ? "green" : "red" }}
            >
              {isMatch ? "Password match" : "Password do not match"}
            </Typography>
          )}
        </CardContent>

        <CardActions>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            // sx={{ mt: 2 }}
            disabled={!isMatch || passwordStrength === "Weak" || loading}
            onClick={handleUpdatePassword}
          >
            {loading ? "Updating..." : "UPDATE PASSWORD"}
          </Button>
        </CardActions>
      </Card>
    </>
  );
};

export default ChangePassword;
